import { useEffect, useState, type FormEvent } from "react";
import { auth, db, eq, mfa, type Factor } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { useSession } from "../lib/session";
import { PageHead } from "./Shell";
import { ErrorBox, Field, Loading, Panel, useToast } from "./ui";

/** Full-screen step after the password when 2FA is on. */
export function MfaChallenge() {
  const { refresh } = useSession();
  const [code, setCode] = useState("");
  const [err, setErr] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const f = (await mfa.factors()).find((x) => x.status === "verified");
      if (!f) throw new Error("No authenticator is set up on this account.");
      await mfa.verify(f.id, code);
      await refresh();
    } catch (e) { setErr(e as Error); } finally { setBusy(false); }
  }
  return (
    <div className="login-form" style={{ minHeight: "100vh" }}>
      <form onSubmit={submit}>
        <img src="/logo.png" alt="" style={{ width: 44 }} />
        <h2>Two-step check</h2>
        <p className="small muted" style={{ margin: 0 }}>Open your authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…) and type the 6-digit code for Royal Compass CRM.</p>
        <ErrorBox error={err} />
        <Field label="6-digit code">
          <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={7} value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))} placeholder="123 456" />
        </Field>
        <button className="btn primary" disabled={busy || code.replace(/\s/g, "").length !== 6}>{busy ? "Checking…" : "Continue"}</button>
        <button type="button" className="btn ghost small" onClick={() => auth.signOut()}>Sign out</button>
      </form>
    </div>
  );
}

/** Set up an authenticator app: QR code → confirm a code. */
export function MfaSetup({ onDone, forced }: { onDone?: () => void; forced?: boolean }) {
  const { refresh } = useSession();
  const toast = useToast();
  const [step, setStep] = useState<{ id: string; qr: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (forced) start(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  async function start() { setErr(null); try { setStep(await mfa.enroll()); } catch (e) { setErr(e as Error); } }
  async function confirm(e: FormEvent) {
    e.preventDefault(); if (!step) return; setBusy(true); setErr(null);
    try { await mfa.verify(step.id, code); toast("Two-step sign-in is on"); await refresh(); invalidate("mfa"); onDone?.(); }
    catch (e) { setErr(e as Error); } finally { setBusy(false); }
  }
  if (!step) return (
    <div className="stack">
      <ErrorBox error={err} />
      <button className="btn primary" onClick={start} style={{ justifySelf: "start" }}>Set up an authenticator app</button>
    </div>
  );
  return (
    <form className="mfa-setup" onSubmit={confirm}>
      <ol className="small">
        <li>Install a free authenticator app on your phone: Google Authenticator, Microsoft Authenticator or 1Password.</li>
        <li>In the app, tap <b>+</b> and scan this code.</li>
        <li>Type the 6-digit code the app shows to finish.</li>
      </ol>
      <div className="mfa-qr"><img src={step.qr} alt="QR code for your authenticator app" /></div>
      <details className="small"><summary>Can't scan? Enter this key instead</summary><code className="mono" style={{ wordBreak: "break-all" }}>{step.secret}</code></details>
      <ErrorBox error={err} />
      <Field label="6-digit code from the app">
        <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))} placeholder="123 456" />
      </Field>
      <button className="btn primary" disabled={busy || code.replace(/\s/g, "").length !== 6}>{busy ? "Checking…" : "Turn on two-step sign-in"}</button>
    </form>
  );
}

/** Shown instead of the app when the owner requires 2FA and this person hasn't set it up. */
export function MfaForceEnroll() {
  return (
    <div className="login-form" style={{ minHeight: "100vh" }}>
      <div style={{ width: "min(440px, 100%)", display: "grid", gap: 14 }}>
        <img src="/logo.png" alt="" style={{ width: 44 }} />
        <h2 style={{ margin: 0, fontFamily: "var(--ff-d)" }}>Protect your account</h2>
        <p className="small muted" style={{ margin: 0 }}>Royal Compass requires two-step sign-in. It takes a minute and keeps client information safe even if a password leaks.</p>
        <MfaSetup forced />
        <button className="btn ghost small" onClick={() => auth.signOut()} style={{ justifySelf: "start" }}>Sign out</button>
      </div>
    </div>
  );
}

/** Settings → Security */
export function SettingsSecurity() {
  const { can, refresh } = useSession();
  const toast = useToast();
  const factors = useQuery("mfa:factors", () => mfa.factors());
  const sec = useQuery("mfa:setting", () => db.one<{ value: any }>("settings", { key: eq("security"), select: "value" }));
  const status = useQuery(can("users", "view") ? "mfa:status" : null, () => db.rpc<{ user_id: string; enabled: boolean }[]>("mfa_status"));
  const users = useQuery(can("users", "view") ? "mfa:users" : null, () => db.select("profiles", { select: "user_id,display_name,email,role_key", order: "display_name" }));
  const verified = (factors.data ?? []).filter((f: Factor) => f.status === "verified");
  const require = !!sec.data?.value?.require_mfa;
  async function remove(f: Factor) {
    if (!window.confirm("Turn off two-step sign-in for your account? You'll only need your password to sign in.")) return;
    await mfa.remove(f.id); invalidate("mfa"); await refresh(); toast("Two-step sign-in turned off");
  }
  async function setRequire(on: boolean) {
    if (on && verified.length === 0) { toast("Set it up on your own account first"); return; }
    await db.update("settings", { key: eq("security") }, { value: { ...(sec.data?.value ?? {}), require_mfa: on } });
    invalidate("mfa"); toast(on ? "Two-step sign-in is now required for everyone" : "Two-step sign-in is optional");
  }
  async function setLimit(k: "idle_minutes" | "max_session_days", v: number) {
    await db.update("settings", { key: eq("security") }, { value: { ...(sec.data?.value ?? {}), [k]: v } });
    invalidate("mfa"); await refresh(); toast("Saved: applies on everyone's next page load");
  }
  return (
    <>
      <PageHead title="Security" sub="Two-step sign-in: after your password, a 6-digit code from an app on your phone. Free, and it stops anyone who only has your password." />
      <div className="grid-2">
        <Panel title="Your account" hint={verified.length ? "Two-step sign-in is on" : "Two-step sign-in is off"}>
          <div className="panel-body">
            {factors.loading && !factors.data ? <Loading rows={2} /> : verified.length ? (
              <div className="stack">
                {verified.map((f) => (
                  <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
                    <span><span className="pill warm">On</span> {f.friendly_name ?? "Authenticator app"} <span className="muted small">· added {new Date(f.created_at).toLocaleDateString()}</span></span>
                    {!require && <button className="btn small danger" onClick={() => remove(f)}>Turn off</button>}
                  </div>
                ))}
                {require && <p className="small muted" style={{ margin: 0 }}>Required for everyone, so it can't be turned off.</p>}
              </div>
            ) : <MfaSetup onDone={() => factors.reload()} />}
          </div>
        </Panel>
        {can("users", "edit") && (
          <Panel title="Everyone on the CRM" hint="Require it so nobody can sign in with a password alone">
            <div className="panel-body stack">
              <label className="switch"><input type="checkbox" checked={require} onChange={(e) => setRequire(e.target.checked)} /><span />Require two-step sign-in for everyone</label>
              <div className="form-grid">
                <label className="field"><span>Sign out after no activity for</span>
                  <select className="select" value={String(sec.data?.value?.idle_minutes ?? 30)} onChange={(e) => setLimit("idle_minutes", Number(e.target.value))}>
                    {[15, 30, 60, 120, 240].map((m) => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? "s" : ""}`}</option>)}
                  </select></label>
                <label className="field"><span>Ask everyone to sign in again every</span>
                  <select className="select" value={String(sec.data?.value?.max_session_days ?? 7)} onChange={(e) => setLimit("max_session_days", Number(e.target.value))}>
                    {[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>{d} day{d > 1 ? "s" : ""}</option>)}
                  </select></label>
              </div>
              <table className="data">
                <thead><tr><th>Person</th><th>Role</th><th>Two-step</th></tr></thead>
                <tbody>{(users.data ?? []).map((u: any) => {
                  const on = status.data?.find((s) => s.user_id === u.user_id)?.enabled;
                  return <tr key={u.user_id}><td>{u.display_name}<div className="small muted">{u.email}</div></td><td>{u.role_key}</td><td>{on ? <span className="pill warm">On</span> : <span className="pill alert">Off</span>}</td></tr>;
                })}</tbody>
              </table>
            </div>
          </Panel>
        )}
      </div>
    </>
  );
}
